import { useState, useCallback, useMemo, useEffect, useRef, type FormEvent } from "react";
import "flag-icons/css/flag-icons.min.css";
import "./PreCheckinApp.css";
import {
    SUPPORTED_LANGUAGES,
    type LanguageKey,
    type TranslationKey,
    getTranslations
} from "./translations";
import { getAuthHeaders } from "../../../../api/auth";
import PfleidererLogo from "../../shared/components/PfleidererLogo.svg";
import AnzeigetafelImage from "./AnzeigetafelPFGT.svg";
import VirtualKeyboard from "./VirtualKeyboard";

// Hide AIpad footer when this component is mounted
function useHideAIpadFooter() {
    useEffect(() => {
        // If running in iframe (Feed container), target parent window body
        const targetBody = window.self !== window.top && window.parent
            ? window.parent.document.body
            : document.body;

        targetBody.classList.add('hide-aipad-footer');

        return () => {
            targetBody.classList.remove('hide-aipad-footer');
        };
    }, []);
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";
const FALLBACK_LANGUAGE: LanguageKey = "en-US";
const AUTO_RESET_SECONDS = 20;
const GUARD_REDIRECT_SECONDS = 15;
const INACTIVITY_TIMEOUT_SECONDS = 30;
const TIME_WINDOW_BUFFER_HOURS = 2; // ±2 hours = one time window

type Stage = "welcome" | "language" | "batchNumber" | "licensePlate" | "redirectToGuard" | "redirectToScale" | "success";

type TimingStatus = "green" | "yellow" | "red";

type RedirectReason = "timing" | "quota" | "precheckin-not-allowed";

interface BatchTransport {
    _id: string;
    transportBatchNumber: string;
    TransportNumber?: string;
    truckLicensePlate?: string;
    carrierName?: string;
    supplierName?: string;
    SupplierName?: string;
    fraction?: string;
    Fraction?: string;
    woodGroup?: string;
    assortment?: string;
    Sortimentbez?: string;
    deliverydate?: string;
    timeWindowFrom?: string;
    timeWindowTo?: string;
    plant?: string;
    Plant?: string;
    CalWeek?: string;
    Contract?: string;
    ContractPos?: string;
    Vertart?: string;
    quantityTrucks?: number;
    qaNote1?: string;
    qaNote2?: string;
    qaNote3?: string;
    qaNote4?: string;
    qaNote5?: string;
    stagingNumber?: string;
    stagingNumber2?: string;
    stagingNumber3?: string;
    stagingNumber4?: string;
    stagingNumber5?: string;
    assortmentPos1?: string;
    assortmentPos2?: string;
    assortmentPos3?: string;
    assortmentPos4?: string;
    assortmentPos5?: string;
    distributionPos1?: number;
    distributionPos2?: number;
    distributionPos3?: number;
    distributionPos4?: number;
    distributionPos5?: number;
}

function resolveLanguageCandidate(fallback: LanguageKey): LanguageKey {
    const browserLang = navigator.language;
    const match = SUPPORTED_LANGUAGES.find((lang) => lang.key === browserLang);
    if (match) return match.key;

    const shortCode = browserLang.split("-")[0];
    const partialMatch = SUPPORTED_LANGUAGES.find((lang) => lang.key.startsWith(shortCode));
    if (partialMatch) return partialMatch.key;

    return fallback;
}

function calculateTimingStatus(timeWindowFrom?: string, timeWindowTo?: string): TimingStatus {
    if (!timeWindowFrom || !timeWindowTo) return "green";

    const now = new Date();
    const windowStart = new Date(timeWindowFrom);
    const windowEnd = new Date(timeWindowTo);

    // Check if within exact time window
    if (now >= windowStart && now <= windowEnd) {
        return "green";
    }

    // Calculate hours offset
    const hoursBeforeStart = (windowStart.getTime() - now.getTime()) / (1000 * 60 * 60);
    const hoursAfterEnd = (now.getTime() - windowEnd.getTime()) / (1000 * 60 * 60);

    // Yellow: ±2 hours (one time window)
    if (hoursBeforeStart > 0 && hoursBeforeStart <= TIME_WINDOW_BUFFER_HOURS) {
        return "yellow";
    }
    if (hoursAfterEnd > 0 && hoursAfterEnd <= TIME_WINDOW_BUFFER_HOURS) {
        return "yellow";
    }

    // Red: >2 hours offset
    return "red";
}

async function loadBatchTransport(
    batchNumber: string,
    language: string
): Promise<BatchTransport> {
    const normalized = batchNumber.trim();
    const batchUrl = `${API_BASE_URL}/api/batch-transports?transportBatchNumber=${encodeURIComponent(normalized)}`;

    const batchResponse = await fetch(batchUrl, {
        headers: {
            ...getAuthHeaders(),
            "Accept-Language": language
        },
        credentials: "include"
    });

    if (!batchResponse.ok) {
        const error = new Error(batchResponse.status === 404 ? "not-found" : "lookup-failed");
        error.name = batchResponse.status === 404 ? "NotFound" : "LookupFailed";
        throw error;
    }

    const batchPayload = await batchResponse.json();
    if (!Array.isArray(batchPayload) || batchPayload.length === 0) {
        const error = new Error("not-found");
        error.name = "NotFound";
        throw error;
    }

    return batchPayload[0];
}

async function createYardDocument(
    batchTransport: BatchTransport,
    truckLicensePlate: string,
    language: string
): Promise<{ id: string; transportNumber: string }> {
    const baseBatchNumber = batchTransport.transportBatchNumber;

    // Find all existing yard documents with this transport batch number
    const searchUrl = `${API_BASE_URL}/api/yard-documents?transportBatchNumber=${encodeURIComponent(baseBatchNumber)}`;
    const searchResponse = await fetch(searchUrl, {
        headers: {
            ...getAuthHeaders(),
            "Accept-Language": language
        },
        credentials: "include"
    });

    if (!searchResponse.ok) {
        throw new Error(`Failed to search yard documents: ${searchResponse.status}`);
    }

    const existingDocuments = await searchResponse.json();

    // Find highest transportNumber suffix
    let maxCounter = 0;
    if (Array.isArray(existingDocuments)) {
        for (const doc of existingDocuments) {
            const transportNumber = doc.transportNumber || "";
            if (transportNumber.startsWith(baseBatchNumber) && transportNumber.length > baseBatchNumber.length) {
                const suffix = transportNumber.substring(baseBatchNumber.length);
                const counter = parseInt(suffix, 10);
                if (!isNaN(counter) && counter > maxCounter) {
                    maxCounter = counter;
                }
            }
        }
    }

    // Generate next transportNumber
    const nextCounter = maxCounter + 1;
    const transportNumber = `${baseBatchNumber}${String(nextCounter).padStart(3, "0")}`;

    // Normalize license plate: remove dashes, spaces and convert to uppercase
    const normalizedLicensePlate = truckLicensePlate.replace(/[-\s]/g, '').toUpperCase();

    // Create yard document with SAP fields - workflow will handle mapping from batchTransport
    const createUrl = `${API_BASE_URL}/api/yard-documents`;
    const createResponse = await fetch(createUrl, {
        method: "POST",
        headers: {
            ...getAuthHeaders(),
            "Content-Type": "application/json",
            "Accept-Language": language
        },
        credentials: "include",
        body: JSON.stringify({
            type: "yardDocument",
            TRANSPORTCHARGE: baseBatchNumber,
            KENNZEICHEN: normalizedLicensePlate,
            LKW_LOAD_STATUS: "1", // Status 1 = Created/Pre-Checkin
            // Workflow will fetch batchTransport and map remaining fields
            qaNote1: batchTransport.qaNote1,
            qaNote2: batchTransport.qaNote2,
            qaNote3: batchTransport.qaNote3,
            qaNote4: batchTransport.qaNote4,
            qaNote5: batchTransport.qaNote5,
            stagingNumber: batchTransport.stagingNumber,
            stagingNumber2: batchTransport.stagingNumber2,
            stagingNumber3: batchTransport.stagingNumber3,
            stagingNumber4: batchTransport.stagingNumber4,
            stagingNumber5: batchTransport.stagingNumber5,
            assortmentPos1: batchTransport.assortmentPos1,
            assortmentPos2: batchTransport.assortmentPos2,
            assortmentPos3: batchTransport.assortmentPos3,
            assortmentPos4: batchTransport.assortmentPos4,
            assortmentPos5: batchTransport.assortmentPos5,
            distributionPos1: batchTransport.distributionPos1,
            distributionPos2: batchTransport.distributionPos2,
            distributionPos3: batchTransport.distributionPos3,
            distributionPos4: batchTransport.distributionPos4,
            distributionPos5: batchTransport.distributionPos5,
            status: 1 // Pre-Check-In status
        })
    });

    if (!createResponse.ok) {
        throw new Error(`Failed to create yard document: ${createResponse.status}`);
    }

    const createdDocument = await createResponse.json();
    return {
        id: createdDocument._id,
        transportNumber
    };
}

export function PreCheckinApp() {
    // Hide AIpad footer (Chat button)
    useHideAIpadFooter();

    const [selectedLanguage, setSelectedLanguage] = useState<LanguageKey>(() =>
        resolveLanguageCandidate(FALLBACK_LANGUAGE)
    );
    const translations = useMemo(() => getTranslations(selectedLanguage), [selectedLanguage]);
    const t = useCallback(
        (key: TranslationKey, params?: Record<string, string | number>) => {
            let text = translations[key] ?? key;
            if (params) {
                Object.entries(params).forEach(([key, value]) => {
                    text = text.replace(`{${key}}`, String(value));
                });
            }
            return text;
        },
        [translations]
    );

    const [stage, setStage] = useState<Stage>("welcome");
    const [batchNumber, setBatchNumber] = useState("");
    const [batchTransport, setBatchTransport] = useState<BatchTransport | null>(null);
    const [timingStatus, setTimingStatus] = useState<TimingStatus>("green");
    const [licensePlate, setLicensePlate] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [autoResetSeconds, setAutoResetSeconds] = useState<number | null>(null);
    const [redirectSeconds, setRedirectSeconds] = useState<number | null>(null);
    const [redirectReason, setRedirectReason] = useState<RedirectReason>("timing");
    const [showLanguageMenu, setShowLanguageMenu] = useState(false);

    // Virtual Keyboard State
    const [showKeyboard, setShowKeyboard] = useState(false);
    const [activeInputField, setActiveInputField] = useState<'batchNumber' | 'licensePlate' | null>(null);
    const activeInputRef = useRef<HTMLInputElement | null>(null);

    const autoResetTimerRef = useRef<number | null>(null);
    const redirectTimerRef = useRef<number | null>(null);
    const inactivityTimerRef = useRef<number | null>(null);

    const resetApp = useCallback(() => {
        setStage("welcome");
        setBatchNumber("");
        setBatchTransport(null);
        setTimingStatus("green");
        setLicensePlate("");
        setError(null);
        setLoading(false);
        setAutoResetSeconds(null);
        setRedirectSeconds(null);
        setSelectedLanguage(resolveLanguageCandidate(FALLBACK_LANGUAGE));

        // Close keyboard
        setShowKeyboard(false);
        setActiveInputField(null);

        if (autoResetTimerRef.current) {
            window.clearInterval(autoResetTimerRef.current);
            autoResetTimerRef.current = null;
        }
        if (redirectTimerRef.current) {
            window.clearInterval(redirectTimerRef.current);
            redirectTimerRef.current = null;
        }
        if (inactivityTimerRef.current) {
            window.clearTimeout(inactivityTimerRef.current);
            inactivityTimerRef.current = null;
        }
    }, []);

    const startAutoReset = useCallback((seconds: number, onComplete: () => void) => {
        setAutoResetSeconds(seconds);
        let remaining = seconds;

        autoResetTimerRef.current = window.setInterval(() => {
            remaining -= 1;
            setAutoResetSeconds(remaining);

            if (remaining <= 0) {
                if (autoResetTimerRef.current) {
                    window.clearInterval(autoResetTimerRef.current);
                    autoResetTimerRef.current = null;
                }
                onComplete();
            }
        }, 1000);
    }, []);

    const startRedirectCountdown = useCallback(() => {
        setRedirectSeconds(GUARD_REDIRECT_SECONDS);
        let remaining = GUARD_REDIRECT_SECONDS;

        redirectTimerRef.current = window.setInterval(() => {
            remaining -= 1;
            setRedirectSeconds(remaining);

            if (remaining <= 0) {
                if (redirectTimerRef.current) {
                    window.clearInterval(redirectTimerRef.current);
                    redirectTimerRef.current = null;
                }
                resetApp();
            }
        }, 1000);
    }, [resetApp]);

    useEffect(() => {
        return () => {
            if (autoResetTimerRef.current) {
                window.clearInterval(autoResetTimerRef.current);
            }
            if (redirectTimerRef.current) {
                window.clearInterval(redirectTimerRef.current);
            }
            if (inactivityTimerRef.current) {
                window.clearTimeout(inactivityTimerRef.current);
            }
        };
    }, []);

    // Inactivity timeout - reset to welcome screen after 30 seconds
    const resetInactivityTimer = useCallback(() => {
        // Don't start timer on welcome screen
        if (stage === "welcome") {
            return;
        }

        // Clear any existing timer
        if (inactivityTimerRef.current) {
            window.clearTimeout(inactivityTimerRef.current);
        }

        // Start new timer
        inactivityTimerRef.current = window.setTimeout(() => {
            resetApp();
        }, INACTIVITY_TIMEOUT_SECONDS * 1000);
    }, [stage, resetApp]);

    // Initialize inactivity timer on stage change
    useEffect(() => {
        resetInactivityTimer();

        // Cleanup on unmount or stage change
        return () => {
            if (inactivityTimerRef.current) {
                window.clearTimeout(inactivityTimerRef.current);
                inactivityTimerRef.current = null;
            }
        };
    }, [resetInactivityTimer]);

    // Reset inactivity timer on user activity (mouse, touch, keyboard)
    useEffect(() => {
        if (stage === "welcome") {
            return;
        }

        const handleUserActivity = () => {
            resetInactivityTimer();
        };

        // Listen for various user activity events
        window.addEventListener('mousemove', handleUserActivity);
        window.addEventListener('mousedown', handleUserActivity);
        window.addEventListener('touchstart', handleUserActivity);
        window.addEventListener('touchmove', handleUserActivity);
        window.addEventListener('keydown', handleUserActivity);
        window.addEventListener('click', handleUserActivity);

        return () => {
            window.removeEventListener('mousemove', handleUserActivity);
            window.removeEventListener('mousedown', handleUserActivity);
            window.removeEventListener('touchstart', handleUserActivity);
            window.removeEventListener('touchmove', handleUserActivity);
            window.removeEventListener('keydown', handleUserActivity);
            window.removeEventListener('click', handleUserActivity);
        };
    }, [stage, resetInactivityTimer]);

    // Close language menu when clicking outside
    useEffect(() => {
        if (!showLanguageMenu) {
            return;
        }

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (!target.closest('.language-switcher')) {
                setShowLanguageMenu(false);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [showLanguageMenu]);

    const handleLanguageSelect = useCallback((lang: LanguageKey) => {
        setSelectedLanguage(lang);
        setShowLanguageMenu(false);
        // Only change stage if we're on language selection
        if (stage === "language") {
            setStage("batchNumber");
        }
    }, [stage]);

    // Close keyboard when stage changes
    useEffect(() => {
        setShowKeyboard(false);
        setActiveInputField(null);
        activeInputRef.current = null;
    }, [stage]);

    // Virtual Keyboard Handlers
    const getInputType = (): 'text' | 'licensePlate' | 'numeric' => {
        if (activeInputField === 'licensePlate') return 'licensePlate';
        if (activeInputField === 'batchNumber') return 'numeric';
        return 'text';
    };

    const handleInputFocus = (field: 'batchNumber' | 'licensePlate', inputElement: HTMLInputElement) => {
        setActiveInputField(field);
        activeInputRef.current = inputElement;
        setShowKeyboard(true);

        // Scroll input into view immediately
        inputElement.scrollIntoView({ behavior: 'auto', block: 'center' });
    };

    const handleKeyboardKeyPress = (key: string) => {
        if (!activeInputField || !activeInputRef.current) return;

        const input = activeInputRef.current;
        const currentValue = activeInputField === 'batchNumber' ? batchNumber : licensePlate;
        const selectionStart = input.selectionStart || 0;
        const selectionEnd = input.selectionEnd || 0;

        let newValue: string;

        if (key === 'Backspace') {
            if (selectionStart === selectionEnd) {
                // Delete single character before cursor
                newValue = currentValue.slice(0, Math.max(0, selectionStart - 1)) + currentValue.slice(selectionEnd);
                setTimeout(() => {
                    input.setSelectionRange(selectionStart - 1, selectionStart - 1);
                }, 0);
            } else {
                // Delete selection
                newValue = currentValue.slice(0, selectionStart) + currentValue.slice(selectionEnd);
                setTimeout(() => {
                    input.setSelectionRange(selectionStart, selectionStart);
                }, 0);
            }
        } else {
            // Insert character at cursor position
            newValue = currentValue.slice(0, selectionStart) + key + currentValue.slice(selectionEnd);
            setTimeout(() => {
                input.setSelectionRange(selectionStart + key.length, selectionStart + key.length);
            }, 0);
        }

        // Update state based on field
        if (activeInputField === 'batchNumber') {
            setBatchNumber(newValue);
        } else if (activeInputField === 'licensePlate') {
            setLicensePlate(newValue.toUpperCase());
        }

        input.focus(); // Keep focus on input
    };

    const handleKeyboardClose = () => {
        setShowKeyboard(false);
        setActiveInputField(null);
        activeInputRef.current = null;
    };

    const handleBatchNumberSubmit = useCallback(async (e: FormEvent) => {
        e.preventDefault();
        setError(null);
        setLoading(true);

        try {
            const batch = await loadBatchTransport(batchNumber, selectedLanguage);
            setBatchTransport(batch);

            const timing = calculateTimingStatus(batch.timeWindowFrom, batch.timeWindowTo);
            setTimingStatus(timing);

            // ✅ NEW: Check ZHBT48GT - PreCheckin erlaubt?
            try {
                const preCheckinCheckUrl = `${API_BASE_URL}/api/batch-transports/validate-precheckin/${encodeURIComponent(batch.transportBatchNumber)}`;
                const preCheckinResponse = await fetch(preCheckinCheckUrl, {
                    headers: getAuthHeaders(),
                    credentials: "include"
                });

                if (preCheckinResponse.ok) {
                    const { preCheckinAllowed } = await preCheckinResponse.json();

                    if (!preCheckinAllowed) {
                        // PreCheckin NICHT erlaubt → Redirect to Scale
                        setRedirectReason("precheckin-not-allowed");
                        setStage("redirectToScale");
                        startRedirectCountdown();
                        setLoading(false);
                        return;
                    }
                } else {
                    console.error("Failed to check PreCheckin permission:", preCheckinResponse.status);
                    // Bei Fehler: Weiter mit normalem Flow (fail-open)
                }
            } catch (err) {
                console.error("Failed to validate PreCheckin permission:", err);
                // Bei Fehler: Weiter mit normalem Flow (fail-open)
            }

            // Check quantityTrucks limit if specified
            if (batch.quantityTrucks) {
                try {
                    const yardDocsResponse = await fetch(
                        `${API_BASE_URL}/api/yard-documents?transportBatchNumber=${encodeURIComponent(batch.transportBatchNumber)}`,
                        { headers: getAuthHeaders() }
                    );

                    if (yardDocsResponse.ok) {
                        const existingDocs = await yardDocsResponse.json();
                        const count = Array.isArray(existingDocs) ? existingDocs.length : 0;

                        if (count >= batch.quantityTrucks) {
                            // Quota exceeded: redirect to guard
                            setTimingStatus("red");
                            setRedirectReason("quota");
                            setStage("redirectToGuard");
                            startRedirectCountdown();
                            setLoading(false);
                            return;
                        }
                    }
                } catch (err) {
                    console.error("Failed to check quantityTrucks quota:", err);
                    // Continue with normal flow if quota check fails
                }
            }

            // Pre-fill license plate if available and proceed to license plate stage
            setLicensePlate(batch.truckLicensePlate?.toUpperCase() || "");
            setStage("licensePlate");
        } catch (err) {
            if (err instanceof Error && err.name === "NotFound") {
                setError(t("batch.error.notFound"));
            } else {
                setError(t("batch.error.failed"));
            }
        } finally {
            setLoading(false);
        }
    }, [batchNumber, selectedLanguage, t, startRedirectCountdown]);

    const handleBatchNumberKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleBatchNumberSubmit(e as any);
        }
    }, [handleBatchNumberSubmit]);

    const handleLicensePlateSubmit = useCallback(async (e: FormEvent) => {
        e.preventDefault();
        if (!batchTransport || !licensePlate.trim()) return;

        setError(null);
        setLoading(true);

        try {
            await createYardDocument(batchTransport, licensePlate, selectedLanguage);
            setStage("success");
            startAutoReset(AUTO_RESET_SECONDS, resetApp);
        } catch (err) {
            setError(t("error.generic"));
        } finally {
            setLoading(false);
        }
    }, [batchTransport, licensePlate, selectedLanguage, t, startAutoReset, resetApp]);

    const handleConfirmRedirect = useCallback(() => {
        resetApp();
    }, [resetApp]);

    // Welcome Screen Stage
    if (stage === "welcome") {
        return (
            <div className="precheckin-root">
                <div className="precheckin-shell">
                    <div className="precheckin-content">
                        <div className="stage-content">
                            <div className="welcome-screen">
                                <img src={PfleidererLogo} alt="Pfleiderer Logo" className="welcome-logo" />
                                <h1 className="welcome-title">Willkommen bei Pfleiderer</h1>
                                <button
                                    type="button"
                                    className="welcome-button"
                                    onClick={() => setStage("language")}
                                >
                                    Start
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                {/* Preload success image in hidden container */}
                <img src={AnzeigetafelImage} alt="" style={{ position: 'absolute', width: '1px', height: '1px', opacity: 0, pointerEvents: 'none' }} />
            </div>
        );
    }

    // Language Selection Stage
    if (stage === "language") {
        return (
            <div className="precheckin-root">
                <div className="precheckin-shell">
                    <div className="precheckin-content">
                        <div className="stage-content">
                            <h2 className="stage-title">{t("language.title")}</h2>
                            <p className="stage-subtitle">{t("language.subtitle")}</p>
                            <div className="language-grid">
                                {SUPPORTED_LANGUAGES.map((lang) => (
                                    <button
                                        key={lang.key}
                                        className="language-card"
                                        onClick={() => handleLanguageSelect(lang.key)}
                                    >
                                        <span className={`fi fi-${lang.flagCode}`}></span>
                                        <span>{lang.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Batch Number Input Stage
    if (stage === "batchNumber") {
        return (
            <div className="precheckin-root">
                <div className="precheckin-shell">
                    <div className="precheckin-header">
                        <div className="language-switcher">
                            <button
                                type="button"
                                className="language-toggle"
                                onClick={() => setShowLanguageMenu(!showLanguageMenu)}
                                aria-label="Change language"
                            >
                                <span className={`fi fi-${SUPPORTED_LANGUAGES.find(l => l.key === selectedLanguage)?.flagCode}`} />
                            </button>
                            {showLanguageMenu && (
                                <div className="language-menu">
                                    {SUPPORTED_LANGUAGES.map((language) => (
                                        <button
                                            key={language.key}
                                            type="button"
                                            className={`language-menu-item${selectedLanguage === language.key ? " active" : ""}`}
                                            onClick={() => handleLanguageSelect(language.key)}
                                        >
                                            <span className={`fi fi-${language.flagCode}`} aria-hidden="true" />
                                            <span>{language.label}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="precheckin-content">
                        <div className="stage-content">
                            <h2 className="stage-title">{t("batch.title")}</h2>
                            <p className="stage-subtitle">{t("batch.subtitle")}</p>
                            <form className="stage-form" onSubmit={handleBatchNumberSubmit}>
                                <label>
                                    <input
                                        type="text"
                                        value={batchNumber}
                                        onChange={(e) => setBatchNumber(e.target.value)}
                                        onKeyDown={handleBatchNumberKeyDown}
                                        onFocus={(e) => handleInputFocus('batchNumber', e.target)}
                                        placeholder={t("batch.placeholder")}
                                        autoFocus
                                        disabled={loading}
                                    />
                                </label>
                                {error && <div className="error-message">{error}</div>}
                                <div className="stage-actions">
                                    <button type="submit" className="btn-primary" disabled={loading || !batchNumber.trim()}>
                                        {loading ? "..." : t("actions.continue")}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
                {/* Virtual Keyboard */}
                <VirtualKeyboard
                    visible={showKeyboard}
                    onKeyPress={handleKeyboardKeyPress}
                    onClose={handleKeyboardClose}
                    inputType={getInputType()}
                />
            </div>
        );
    }

    // Redirect to Guard Stage (Yellow/Red timing or quota exceeded)
    if (stage === "redirectToGuard") {
        const titleKey = redirectReason === "quota" ? "redirect.quotaTitle" : "redirect.title";
        const messageKey = redirectReason === "quota" ? "redirect.quotaMessage" : "redirect.message";

        return (
            <div className="precheckin-root">
                <div className="precheckin-shell">
                    <div className="precheckin-header">
                        <div className="language-switcher">
                            <button
                                type="button"
                                className="language-toggle"
                                onClick={() => setShowLanguageMenu(!showLanguageMenu)}
                                aria-label="Change language"
                            >
                                <span className={`fi fi-${SUPPORTED_LANGUAGES.find(l => l.key === selectedLanguage)?.flagCode}`} />
                            </button>
                            {showLanguageMenu && (
                                <div className="language-menu">
                                    {SUPPORTED_LANGUAGES.map((language) => (
                                        <button
                                            key={language.key}
                                            type="button"
                                            className={`language-menu-item${selectedLanguage === language.key ? " active" : ""}`}
                                            onClick={() => handleLanguageSelect(language.key)}
                                        >
                                            <span className={`fi fi-${language.flagCode}`} aria-hidden="true" />
                                            <span>{language.label}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="precheckin-content">
                        <div className="stage-content">
                            <div className="redirect-card">
                                <h2>{t(titleKey)}</h2>
                                <p>{t(messageKey)}</p>
                                {redirectSeconds !== null && (
                                    <div className="redirect-countdown">
                                        <span>{t("redirect.countdown", { seconds: redirectSeconds })}</span>
                                        <div className="redirect-countdown-track">
                                            <div
                                                className="redirect-countdown-progress"
                                                style={{ width: `${(redirectSeconds / GUARD_REDIRECT_SECONDS) * 100}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                )}
                                <div className="stage-actions">
                                    <button className="btn-primary" onClick={handleConfirmRedirect}>
                                        {t("redirect.confirm")}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Redirect to Scale Stage (PreCheckin not allowed)
    if (stage === "redirectToScale") {
        return (
            <div className="precheckin-root">
                <div className="precheckin-shell">
                    <div className="precheckin-header">
                        <div className="language-switcher">
                            <button
                                type="button"
                                className="language-toggle"
                                onClick={() => setShowLanguageMenu(!showLanguageMenu)}
                                aria-label="Change language"
                            >
                                <span className={`fi fi-${SUPPORTED_LANGUAGES.find(l => l.key === selectedLanguage)?.flagCode}`} />
                            </button>
                            {showLanguageMenu && (
                                <div className="language-menu">
                                    {SUPPORTED_LANGUAGES.map((language) => (
                                        <button
                                            key={language.key}
                                            type="button"
                                            className={`language-menu-item${selectedLanguage === language.key ? " active" : ""}`}
                                            onClick={() => handleLanguageSelect(language.key)}
                                        >
                                            <span className={`fi fi-${language.flagCode}`} aria-hidden="true" />
                                            <span>{language.label}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="precheckin-content">
                        <div className="stage-content">
                            <div className="redirect-card redirect-scale">
                                <h2>{t("scale.title")}</h2>
                                <p>{t("scale.message")}</p>
                                {redirectSeconds !== null && (
                                    <div className="redirect-countdown">
                                        <span>{t("redirect.countdown", { seconds: redirectSeconds })}</span>
                                        <div className="redirect-countdown-track">
                                            <div
                                                className="redirect-countdown-progress"
                                                style={{ width: `${(redirectSeconds / GUARD_REDIRECT_SECONDS) * 100}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                )}
                                <div className="stage-actions">
                                    <button className="btn-primary" onClick={handleConfirmRedirect}>
                                        {t("scale.confirm")}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // License Plate Input Stage
    if (stage === "licensePlate") {
        return (
            <div className="precheckin-root">
                <div className="precheckin-shell">
                    <div className="precheckin-header">
                        <div className="language-switcher">
                            <button
                                type="button"
                                className="language-toggle"
                                onClick={() => setShowLanguageMenu(!showLanguageMenu)}
                                aria-label="Change language"
                            >
                                <span className={`fi fi-${SUPPORTED_LANGUAGES.find(l => l.key === selectedLanguage)?.flagCode}`} />
                            </button>
                            {showLanguageMenu && (
                                <div className="language-menu">
                                    {SUPPORTED_LANGUAGES.map((language) => (
                                        <button
                                            key={language.key}
                                            type="button"
                                            className={`language-menu-item${selectedLanguage === language.key ? " active" : ""}`}
                                            onClick={() => handleLanguageSelect(language.key)}
                                        >
                                            <span className={`fi fi-${language.flagCode}`} aria-hidden="true" />
                                            <span>{language.label}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="precheckin-content">
                        <div className="stage-content">
                            <h2 className="stage-title">{t("license.title")}</h2>
                            <p className="stage-subtitle">{t("license.subtitle")}</p>
                            <form className="stage-form" onSubmit={handleLicensePlateSubmit}>
                                <label>
                                    <input
                                        type="text"
                                        value={licensePlate}
                                        onChange={(e) => setLicensePlate(e.target.value.toUpperCase())}
                                        onFocus={(e) => handleInputFocus('licensePlate', e.target)}
                                        placeholder={t("license.placeholder")}
                                        disabled={loading}
                                    />
                                </label>
                                {error && <div className="error-message">{error}</div>}
                                <div className="stage-actions">
                                    <button type="submit" className="btn-primary" disabled={loading || !licensePlate.trim()}>
                                        {loading ? "..." : t("actions.continue")}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
                {/* Virtual Keyboard */}
                <VirtualKeyboard
                    visible={showKeyboard}
                    onKeyPress={handleKeyboardKeyPress}
                    onClose={handleKeyboardClose}
                    inputType={getInputType()}
                />
            </div>
        );
    }

    // Success Stage
    if (stage === "success") {
        return (
            <div className="precheckin-root">
                <div className="precheckin-shell">
                    <div className="precheckin-header">
                        <div className="language-switcher">
                            <button
                                type="button"
                                className="language-toggle"
                                onClick={() => setShowLanguageMenu(!showLanguageMenu)}
                                aria-label="Change language"
                            >
                                <span className={`fi fi-${SUPPORTED_LANGUAGES.find(l => l.key === selectedLanguage)?.flagCode}`} />
                            </button>
                            {showLanguageMenu && (
                                <div className="language-menu">
                                    {SUPPORTED_LANGUAGES.map((language) => (
                                        <button
                                            key={language.key}
                                            type="button"
                                            className={`language-menu-item${selectedLanguage === language.key ? " active" : ""}`}
                                            onClick={() => handleLanguageSelect(language.key)}
                                        >
                                            <span className={`fi fi-${language.flagCode}`} aria-hidden="true" />
                                            <span>{language.label}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="precheckin-content">
                        <div className="stage-content">
                            <div className="success-card">
                                <h2>{t("success.title")}</h2>
                                <p>{t("success.message")}</p>
                                <img src={AnzeigetafelImage} alt="Anzeigetafel" className="success-image" />
                                {autoResetSeconds !== null && (
                                    <div className="auto-reset-notice">
                                        <span>{t("success.autoReset", { seconds: autoResetSeconds })}</span>
                                        <div className="auto-reset-track">
                                            <div
                                                className="auto-reset-progress"
                                                style={{ width: `${(autoResetSeconds / AUTO_RESET_SECONDS) * 100}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                {/* Virtual Keyboard */}
                <VirtualKeyboard
                    visible={showKeyboard}
                    onKeyPress={handleKeyboardKeyPress}
                    onClose={handleKeyboardClose}
                    inputType={getInputType()}
                />
            </div>
        );
    }

    return null;
}
